import forge from 'node-forge';
import { getEnvVariable } from '@ciam/env';
import type { GigyaGetAccountInfo } from '@ciam/gigya-api';
import { GigyaAPI } from '@ciam/gigya-api';
import { create } from 'zustand';
import { defaultInstance } from '@ciam/async-utils';

const ERROR_CODES = {
  SUCCESS: 0,
  PENDING_REGISTRATION: 206001,
  CHANGE_PASSWORD: 403100,
  REQUIRED_CAPTCHA: 401020,
  WRONG_CAPTCHA: 401021,
  INVALID_PARAMETER_VALUE: 400006,
  ACCOUNT_LOCKED: 403120,
  EMAIL_OR_PASSWORD_INCORRECT: 403042,
} as const;

export type UserInfo = {
  email: string;
  uid: string;
  uidSignature: string;
  signatureTimestamp: string;
  isAdLogin: boolean;
} & GigyaGetAccountInfo;

type LoginResult =
  | { success: true; loginInfo?: UserInfo }
  | {
      success: false;
      errorMessage?: string;
      shouldShowCaptcha?: boolean;
      shouldResetCaptcha?: boolean;
      error?: unknown;
    };

type State = {
  currentUser: UserInfo;
};

type Action = {
  setCurrentUser: (userInfo: UserInfo) => void;
  normalLogin: (params: {
    email: string;
    password: string;
    captchaToken?: string;
    channel?: string;
  }) => Promise<LoginResult>;
  adLogin: (params: {
    email: string;
    uid: string;
    uidSignature: string;
    signatureTimestamp: string;
    errorCode: string;
    regToken?: string;
  }) => Promise<LoginResult>;
  clearLoginInfo: () => void;
  finalizeRegistration: (regToken: string) => Promise<LoginResult>;
  encryptPassword: (password: string) => string;
};

type LoginStore = State & Action;

const ERROR_RESPONSES: Record<number, Extract<LoginResult, { success: false }>> = {
  [ERROR_CODES.REQUIRED_CAPTCHA]: {
    success: false,
    errorMessage: 'required-captcha',
    shouldShowCaptcha: true,
  },
  [ERROR_CODES.WRONG_CAPTCHA]: {
    success: false,
    errorMessage: 'wrong-captcha',
    shouldResetCaptcha: true,
  },
  [ERROR_CODES.CHANGE_PASSWORD]: {
    success: false,
    errorMessage: 'change-password',
  },
  [ERROR_CODES.INVALID_PARAMETER_VALUE]: {
    success: false,
    errorMessage: 'invalid-parameter-value',
  },
  [ERROR_CODES.ACCOUNT_LOCKED]: {
    success: false,
    errorMessage: 'account-locked',
  },
  [ERROR_CODES.EMAIL_OR_PASSWORD_INCORRECT]: {
    success: false,
    errorMessage: 'email-or-password-incorrect',
  },
};

export const useLoginStore = create<LoginStore>()((set, get) => {
  // 에러 코드 처리
  const handleErrorCode = (errorCode: number | string): LoginResult => {
    const numericErrorCode = typeof errorCode === 'string' ? parseInt(errorCode) : errorCode;
    return (
      ERROR_RESPONSES[numericErrorCode] || {
        success: false,
        errorMessage: 'default',
      }
    );
  };

  // 로그인 성공 처리 (공통)
  const handleLoginSuccess = (userInfo: UserInfo): LoginResult => {
    set({ currentUser: userInfo });
    return {
      success: true,
      loginInfo: userInfo,
    };
  };

  const initialState = {
    currentUser: {
      email: '',
      uid: '',
      uidSignature: '',
      signatureTimestamp: '',
      isAdLogin: false,
    },
  };

  return {
    ...initialState,
    setCurrentUser: (userInfo) => set({ currentUser: userInfo }),

    adLogin: async ({ email, uid, uidSignature, signatureTimestamp, errorCode, regToken }) => {
      switch (errorCode) {
        case '0':
          return handleLoginSuccess({ email, uid, uidSignature, signatureTimestamp, isAdLogin: true });
        case '200601':
          return await get().finalizeRegistration(regToken as string);
        default:
          return handleErrorCode(errorCode);
      }
    },

    normalLogin: async ({ email, password, captchaToken, channel }) => {
      try {
        const { data: loginData } = (await defaultInstance.post('/secure/login', {
          loginID: email,
          channel: channel,
          encryptedPassword: get().encryptPassword(password),
          ...(captchaToken && {
            captchaToken,
            captchaType: 'recaptcha_v2',
          }),
        })) as {
          data: {
            errorCode: number;
            UID: string;
            UIDSignature: string;
            signatureTimestamp: string;
            regToken?: string;
          };
        };

        switch (loginData.errorCode) {
          case ERROR_CODES.SUCCESS:
            return handleLoginSuccess({
              email,
              uid: loginData.UID,
              uidSignature: loginData.UIDSignature,
              signatureTimestamp: loginData.signatureTimestamp,
              isAdLogin: false,
            });
          case ERROR_CODES.PENDING_REGISTRATION:
            return await get().finalizeRegistration(loginData.regToken as string);
          default:
            return handleErrorCode(loginData.errorCode);
        }
      } catch (error) {
        return { success: false, errorMessage: 'default', error };
      }
    },
    clearLoginInfo: () => set({ currentUser: initialState.currentUser }),
    finalizeRegistration: async (regToken: string) => {
      try {
        await GigyaAPI.accounts.finalizeRegistration({ regToken });
        return {
          success: true,
        };
      } catch (error) {
        return {
          success: false,
          errorMessage: 'Failed to finalize registration',
          error,
        };
      }
    },
    encryptPassword: (password: string) => {
      const publicKeyPem = getEnvVariable('VITE_RSA_PUBLIC_KEY');

      const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
      const encrypted = publicKey.encrypt(password, 'RSA-OAEP', {
        md: forge.md.sha256.create(),
      });
      const base64Encrypted = forge.util.encode64(encrypted);

      return base64Encrypted;
    },
  };
});

export const clearLoginInfo = useLoginStore.getState().clearLoginInfo;
